import {
  CyclabilityZone,
  ExportData,
  ExportDataProjection,
  Partner,
  Period,
  PoiCategory,
  Report,
  ReportType,
  User,
} from '@geovelo-frontends/commons';
import { Map } from 'maplibre-gl';
import { Moment } from 'moment';
import { createContext } from 'react';

import { ICountingSite, ICountingSourceState, TCountingDisplayMode } from '../pages/counts/types';
import { TZoneStatsMap } from '../utils/zone-stats';

import { TTab } from './router/tabs';

export interface IUserContext {
  current?: User | null;
  partners?: Partner[] | null;
  signInDialogOpen: boolean;
}

export const worldId = 0;
export const world = new CyclabilityZone(worldId, 'world');

export interface IZoneContext {
  map: { [id: number]: CyclabilityZone };
  codeMap: { [key: string]: number | null };
  parentMap: { [id: number]: number | null };
  childrenMap: { [id: number]: number[] | null };
  current?: CyclabilityZone | null;
}

export interface IStatsContext {
  periods?: Array<{ key: string; date: Moment }>;
  selectedKey: string | null;
  selectedIndex: number | null;
  byZone: TZoneStatsMap;
  canLoadOlder: boolean;
  loadingOlder: boolean;
  exportDataTypes?: ExportData[];
  exportDataProjections?: ExportDataProjection[];
}

export interface IReportContext {
  reports?: Report[];
  types?: ReportType[];
  allTypes?: ReportType[];
  selectedReport?: Report | null;
  selectedPeriod: Period | null;
}

export interface IPoiContext {
  categories?: PoiCategory[];
}

export interface ICountsContext {
  displayMode: TCountingDisplayMode;
  sites: ICountingSite[];
  selectedSiteId: string | null;
  sources: ICountingSourceState[];
  loading: boolean;
}

export interface IRoutingContext {
  countryCode?: string | null;
  zoneCode?: string | null;
  currentTab: TTab;
  contributionId: number | null;
}

interface IAppActions {
  setMap: (map: Map) => void;
  setCurrentUser: (user?: User | null) => void;
  openSignInDialog: (open: boolean) => void;
  setZoneMap: (map: { [id: number]: CyclabilityZone }) => void;
  setZoneCodeMap: (map: { [key: string]: number | null }) => void;
  setZoneParentMap: (map: { [id: number]: number | null }) => void;
  setZoneChildrenMap: (map: { [id: number]: number[] | null }) => void;
  setCurrentZone: (zone?: CyclabilityZone | null) => void;
  setStatsPeriods: (dates?: Array<{ key: string; date: Moment }>) => void;
  selectStatsKey: (key: string | null) => void;
  selectStatsIndex: (index: number | null) => void;
  loadOlderStats: () => void;
  setCountryCode: (code: string | null) => void;
  setZoneCode: (code: string | null) => void;
  setCurrentTab: (tab: TTab) => void;
  setContributionId: (id: number | null) => void;
  setReports: (reports?: Report[]) => void;
  setSelectedReport: (report?: Report | null) => void;
  setSelectedPeriod: (period: Period) => void;
  setCountingSites: (sites: ICountingSite[]) => void;
  setCountingSources: (sources: ICountingSourceState[]) => void;
  setCountingDisplayMode: (mode: TCountingDisplayMode) => void;
  setSelectedCountingSiteId: (id: string | null) => void;
  setCountingLoading: (loading: boolean) => void;
}

interface IAppContext {
  map: { current?: Map };
  user: IUserContext;
  zone: IZoneContext;
  stats: IStatsContext;
  report: IReportContext;
  pois: IPoiContext;
  counts: ICountsContext;
  routing: IRoutingContext;
  actions: IAppActions;
}

export const AppContext = createContext<IAppContext>({
  map: {},
  user: { signInDialogOpen: false },
  zone: { map: {}, codeMap: {}, parentMap: {}, childrenMap: {} },
  stats: {
    selectedKey: null,
    selectedIndex: null,
    byZone: {},
    canLoadOlder: false,
    loadingOlder: false,
  },
  report: { selectedReport: null, selectedPeriod: null },
  pois: {},
  counts: {
    displayMode: 'sites',
    sites: [],
    selectedSiteId: null,
    sources: [],
    loading: false,
  },
  routing: { currentTab: 'stats', contributionId: null },
  actions: {
    setMap: () => undefined,
    setCurrentUser: () => undefined,
    openSignInDialog: () => undefined,
    setZoneMap: () => undefined,
    setZoneCodeMap: () => undefined,
    setZoneParentMap: () => undefined,
    setZoneChildrenMap: () => undefined,
    setCurrentZone: () => undefined,
    setStatsPeriods: () => undefined,
    selectStatsKey: () => undefined,
    selectStatsIndex: () => undefined,
    loadOlderStats: () => undefined,
    setCountryCode: () => undefined,
    setZoneCode: () => undefined,
    setCurrentTab: () => undefined,
    setContributionId: () => undefined,
    setReports: () => undefined,
    setSelectedReport: () => undefined,
    setSelectedPeriod: () => undefined,
    setCountingSites: () => undefined,
    setCountingSources: () => undefined,
    setCountingDisplayMode: () => undefined,
    setSelectedCountingSiteId: () => undefined,
    setCountingLoading: () => undefined,
  },
});
